
import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { SystemStats } from '../types';
import { backendService } from '../services/backendService';

interface SystemMonitorProps {
  stats: SystemStats;
  setStats: React.Dispatch<React.SetStateAction<SystemStats>>;
}

const SystemMonitor: React.FC<SystemMonitorProps> = ({ stats }) => {
  const [history, setHistory] = useState<any[]>([]);
  const [watchdogData, setWatchdogData] = useState<any>(null);
  const [watchdogError, setWatchdogError] = useState<string | null>(null);

  // Fetch watchdog status
  useEffect(() => {
    const fetchWatchdog = async () => {
      try {
        const data = await backendService.getWatchdogStatus();
        setWatchdogData(data);
        setWatchdogError(null);
      } catch (err) {
        setWatchdogError(err instanceof Error ? err.message : 'Failed to fetch watchdog status');
      }
    };

    fetchWatchdog();
    const interval = setInterval(fetchWatchdog, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timestamp = new Date().toLocaleTimeString();
    setHistory(prev => {
      const newEntry = {
        time: timestamp,
        gpu: stats.gpuLoad,
        cpu: stats.cpuLoad,
        throughput: stats.throughput
      };
      // Prevent rapid identical state updates from clogging history
      if (prev.length > 0 && prev[prev.length - 1].throughput === newEntry.throughput && prev[prev.length - 1].cpu === newEntry.cpu) {
        return prev;
      }
      return [...prev, newEntry].slice(-40);
    });
  }, [stats.cpuLoad, stats.gpuLoad, stats.throughput]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-3 bg-[#1e222d] p-5 rounded border border-[#2a2e39]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
            <i className="fas fa-chart-area text-purple-500"></i> Load Distribution
          </h3>
          <div className="flex gap-4 text-[9px] font-bold">
            <span className="flex items-center gap-1.5 text-purple-400"><div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div> GPU</span>
            <span className="flex items-center gap-1.5 text-orange-400"><div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div> CPU</span>
          </div>
        </div>
        <div className="h-64 w-full min-h-[256px]">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <AreaChart data={history}>
              <defs>
                <linearGradient id="colorGpu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e39" vertical={false} />
              <XAxis dataKey="time" hide />
              <YAxis domain={[0, 100]} stroke="#4c525e" fontSize={9} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ backgroundColor: '#131722', borderColor: '#2a2e39', fontSize: '9px', borderRadius: '4px' }}
                itemStyle={{ color: '#d1d4dc', padding: '2px 0' }}
              />
              <Area type="monotone" dataKey="gpu" stroke="#a855f7" fillOpacity={1} fill="url(#colorGpu)" strokeWidth={2} isAnimationActive={false} />
              <Area type="monotone" dataKey="cpu" stroke="#f97316" fillOpacity={1} fill="url(#colorCpu)" strokeWidth={1.5} strokeDasharray="5 5" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="space-y-4">
        {/* Watchdog Status */}
        <div className="bg-[#1e222d] p-4 rounded border border-[#2a2e39]">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">System Watchdog</p>
            {watchdogData && (
              <span className={`text-xs font-bold ${watchdogData.overall === 'OK' ? 'text-green-400' :
                watchdogData.overall === 'WARNING' ? 'text-yellow-400' : 'text-red-400'
                }`}>
                {watchdogData.overall}
              </span>
            )}
          </div>

          {watchdogError ? (
            <div className="text-red-400 text-xs">Error: {watchdogError}</div>
          ) : watchdogData ? (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-slate-500">RAM</div>
                  <div className={`font-mono ${watchdogData.ram.status === 'OK' ? 'text-green-400' :
                    watchdogData.ram.status === 'WARNING' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {watchdogData.ram.percent.toFixed(0)}%
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">CPU</div>
                  <div className={`font-mono ${watchdogData.cpu.status === 'OK' ? 'text-green-400' :
                    watchdogData.cpu.status === 'WARNING' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {watchdogData.cpu.percent.toFixed(0)}%
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">VRAM</div>
                  <div className={`font-mono ${watchdogData.vram.status === 'OK' ? 'text-green-400' :
                    watchdogData.vram.status === 'WARNING' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {watchdogData.vram.percent.toFixed(0)}%
                  </div>
                </div>
              </div>

              <div className="text-[8px] text-slate-500 mt-2">
                Last check: {watchdogData.last_check}
              </div>
            </div>
          ) : (
            <div className="text-slate-500 text-xs animate-pulse">Loading...</div>
          )}
        </div>
        <div className="bg-[#1e222d] p-4 rounded border border-[#2a2e39]">
          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-3">Resource Status</p>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-[9px] mb-1.5 font-mono">
                <span className="text-slate-500 uppercase">VRAM</span>
                <span className="text-purple-400 font-bold">{stats.vramUsed.toFixed(1)}G / {stats.vramTotal.toFixed(1)}G</span>
              </div>
              <div className="w-full bg-[#131722] rounded-full h-1">
                <div className="bg-purple-500 h-1 rounded-full transition-all duration-700 shadow-[0_0_8px_#a855f7]" style={{ width: `${Math.min(100, (stats.vramUsed / stats.vramTotal) * 100)}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-[9px] mb-1.5 font-mono">
                <span className="text-slate-500 uppercase">RAM Pool</span>
                <span className="text-yellow-500 font-bold">{stats.ramUsed.toFixed(1)}G / {stats.ramTotal.toFixed(1)}G</span>
              </div>
              <div className="w-full bg-[#131722] rounded-full h-1">
                <div className="bg-yellow-500 h-1 rounded-full transition-all duration-700 shadow-[0_0_8px_#eab308]" style={{ width: `${Math.min(100, (stats.ramUsed / stats.ramTotal) * 100)}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#1e222d] p-4 rounded border border-[#2a2e39] flex-1 flex flex-col justify-between">
          <div>
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Aggregate Flow</p>
            <p className="text-2xl font-black text-white mt-1 font-mono tracking-tighter italic">
              {stats.throughput.toLocaleString()} <span className="text-[10px] text-slate-500 font-bold not-italic">TKN</span>
            </p>
          </div>
          <div className="h-12 w-full mt-2">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={50}>
              <LineChart data={history.slice(-15)}>
                <Line type="monotone" dataKey="throughput" stroke="#22c55e" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMonitor;
